import React from 'react';
import styled from '@emotion/styled';
import { ButtonProps, ButtonVariant, ButtonAction } from './types';
import { LinkProps } from '../../../containers/Link/types';
import {
  OL_COLOUR_BLACK_4,
  OL_COLOUR_BLACK_25,
  OL_COLOUR_BLACK_80,
  OL_COLOUR_BLACK_10,
  OL_COLOUR_CTA_PINK,
  OL_COLOUR_KELP,
  OL_COLOUR_DANGER,
  OL_COLOUR_BLACK_90,
  OL_COLOUR_BLACK_55,
} from '../../Style/colours';
import { RouteLink } from '../../RouteLink/component';

const PrimaryButtonStyle = `
  background: linear-gradient(
    355.89deg,
    #e14546 2.4%,
    #ca2e2f 2.41%,
    #d20b70 80.44%
  );
  border-radius: 34px;
  margin: 8px;
  color: #ffffff;
  border: none;
  box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  :hover {
    opacity: 0.9;
  }
  :hover,
  :visited,
  :active,
  :focus {
    color: #ffffff;
  }
`;

const PrimaryOutlineButtonStyle = `
  background: #ffffff;
  border-radius: 34px;
  margin: 8px;
  color: #cf1b53;
  border: none;
  box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  :hover {
    opacity: 0.9;
  }
  :hover,
  :visited,
  :active,
  :focus {
    color: #cf1b53;
  }
`;

const SecondaryButtonStyle = `
  background: rgba(251, 233, 238, 1);
  border-radius: 34px;
  margin: 8px;
  line-height: 140%;
  color: #cf1b53;
  cursor: pointer;
  :hover {
    opacity: 0.9;
  }
  :hover,
  :visited,
  :active,
  :focus {
    color: #cf1b53;
  }
`;

const TertiaryButtonStyle = `
  background: #e5eaeb;
  border-radius: 34px;
  color: #3e4b4c;
  margin: 8px;
  :hover {
    opacity: 0.9;
  }
  :hover,
  :visited,
  :active,
  :focus {
    color: #3e4b4c;
  }
`;

const StyledClickableArea = `
  border: none;
  cursor: pointer;
  display: inline-block;
  font-family: Avenir, sans-serif;
  font-style: normal;
  font-weight: 900;
  font-size: 16px;
  outline: none;
  padding: 15px 27px;
`;

const ATagLink = styled('a')();
const DefaultButton = styled('button')();

const LegacyButtonStyle = `
  background-color: ${OL_COLOUR_BLACK_4};
  border: 1px solid ${OL_COLOUR_BLACK_25};
  color: ${OL_COLOUR_BLACK_80};
  font-weight: 600;
  font-size: 13px;
  border-radius: 2px;
  text-shadow: none;
  padding: 6px 12px;

  &:hover {
    background-color: ${OL_COLOUR_BLACK_10};
    color: ${OL_COLOUR_BLACK_80};
  }

  &[disabled] {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const LegacyButtonCTAPinkStyle = `
  background-color: ${OL_COLOUR_CTA_PINK};
  border-color: ${OL_COLOUR_CTA_PINK};
  color: white;
  text-shadow: none;

  &:hover {
    color: white;
    background-color: ${OL_COLOUR_CTA_PINK};
    border-color: ${OL_COLOUR_CTA_PINK};
  }
`;

const LegacyButtonKelpStyle = `
  background-color: ${OL_COLOUR_KELP};
  border-color: ${OL_COLOUR_KELP};
  color: white;
  text-shadow: none;

  &:hover {
    color: white;
    background-color: ${OL_COLOUR_KELP};
    border-color: ${OL_COLOUR_KELP};
  }
`;

const LegacyButtonDangerStyle = `
  background-color: ${OL_COLOUR_DANGER};
  border-color: ${OL_COLOUR_DANGER};
  color: white;
  text-shadow: none;

  &:hover {
    color: white;
    background-color: ${OL_COLOUR_DANGER};
    border-color: ${OL_COLOUR_DANGER};
  }
`;

// TODO: clean up Button component, so it won't be under marketing
const PlatformButtonStyle = `
  font-family: inherit;
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  border-width: 1px;
  border-style: solid;
  border-radius: 4px;
  padding: 12px 20px;
`;

const PlatformPrimaryButtonStyle = `
  border-color: ${OL_COLOUR_KELP};
  background: ${OL_COLOUR_KELP};
  color: #ffffff;

  &:hover {
    border-color: #07565F;
    background: #07565F;
    color: #ffffff;
  }

  &:disabled {
    border-color: ${OL_COLOUR_BLACK_10};
    background: #ffffff;
    color: ${OL_COLOUR_BLACK_55};
  }
`;

const PlatformSecondaryButtonStyle = `
  border-color: ${OL_COLOUR_BLACK_55};
  background: ${OL_COLOUR_BLACK_4};
  color: ${OL_COLOUR_BLACK_90};

  &:hover {
    background: #ffffff;
    color: ${OL_COLOUR_BLACK_90};
  }

  &:disabled {
    border-color: ${OL_COLOUR_BLACK_10};
    background: #ffffff;
    color: ${OL_COLOUR_BLACK_55};
  }
`;

function getVariantStyle(variant: ButtonVariant): string[] {
  let style: string[];

  switch (variant) {
    case 'primary':
      style = [PrimaryButtonStyle];
      break;
    case 'primary-outline':
      style = [PrimaryOutlineButtonStyle];
      break;
    case 'secondary':
      style = [SecondaryButtonStyle];
      break;
    case 'tertiary':
      style = [TertiaryButtonStyle];
      break;
    case 'legacy-primary':
      style = [LegacyButtonStyle];
      break;
    case 'legacy-cta-pink':
      style = [LegacyButtonStyle, LegacyButtonCTAPinkStyle];
      break;
    case 'legacy-kelp':
      style = [LegacyButtonStyle, LegacyButtonKelpStyle];
      break;
    case 'legacy-danger':
      style = [LegacyButtonStyle, LegacyButtonDangerStyle];
      break;
    case 'platform-primary':
      style = [PlatformButtonStyle, PlatformPrimaryButtonStyle];
      break;
    case 'platform-secondary':
      style = [PlatformButtonStyle, PlatformSecondaryButtonStyle];
      break;
    default:
      style = [];
  }

  return style;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getContentElement(action?: ButtonAction): React.ComponentType<any> {
  if (!action) {
    return DefaultButton;
  }

  switch (action.kind) {
    case 'external_link':
      return ATagLink;
    case 'internal_link':
      return RouteLink;
    case 'on_click':
    default:
      return DefaultButton;
  }
}

function getContentElementProps(
  action?: ButtonAction
): Record<string, unknown> | LinkProps {
  if (!action) {
    return {};
  }

  if (action.kind === 'external_link') {
    const { value, target = '_self' } = action;

    return {
      href: value,
      target,
    };
  }

  if (action.kind === 'internal_link') {
    return action.value.link;
  }

  return { onClick: action.value };
}

export const Button = React.memo(
  ({
    children,
    variant = null,
    className,
    action,
    disabled = false,
  }: ButtonProps) => {
    const ButtonVariantStyle: string[] = getVariantStyle(variant);
    const ButtonContentElement = getContentElement(action);
    const ButtonElement = styled(ButtonContentElement)([
      StyledClickableArea,
      ...ButtonVariantStyle,
    ]);
    const props = getContentElementProps(action);
    return (
      <ButtonElement className={className} disabled={disabled} {...props}>
        {children}
      </ButtonElement>
    );
  }
);
